import { forwardRef, useRef, useEffect } from 'react';
import { useDynamicPopupPosition } from '@/hooks/useDynamicPopupPosition';

export interface DynamicPopupProps {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  isVisible: boolean;
  preferredPosition?: 'left' | 'right' | 'top' | 'bottom';
  offset?: number;
  viewportPadding?: number;
  className?: string;
  style?: React.CSSProperties;
}

const DynamicPopup = forwardRef<HTMLDivElement, DynamicPopupProps>(
  (
    {
      children,
      triggerRef,
      isVisible,
      preferredPosition = 'right',
      offset = 8,
      viewportPadding = 16,
      className = '',
      style = {}
    },
    ref
  ) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const combinedRef = (ref as React.RefObject<HTMLDivElement>) || popupRef;

    const { position, isPositioned } = useDynamicPopupPosition({
      triggerRef,
      popupRef: combinedRef,
      isVisible,
      preferredPosition,
      offset,
      viewportPadding
    });

    // Combine refs
    useEffect(() => {
      if (ref && typeof ref === 'function') {
        ref(combinedRef.current);
      } else if (ref && 'current' in ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = combinedRef.current;
      }
    }, [ref, combinedRef]);

    if (!isVisible) {
      return null;
    }

    const dynamicStyle: React.CSSProperties = {
      position: 'fixed',
      zIndex: 50,
      ...position,
      ...style
    };

    return (
      <div
        ref={combinedRef}
        className={`${className} ${!isPositioned ? 'opacity-0' : 'opacity-100'} transition-opacity duration-150`}
        style={dynamicStyle}
      >
        {children}
      </div>
    );
  }
);

DynamicPopup.displayName = 'DynamicPopup';

export default DynamicPopup;
